import type { Topping, WaffleKind } from '../game/types';
import { drawWaffle } from './icons';
import { mixColor, noise, px } from './sprite';

/* Live-drawn kitchen stations: the flat-top grill and the waffle irons. */

export const SLOT_W = 44;
export const SLOT_H = 30;
export const IRON_W = 36;
export const IRON_H = 30;

const RAMP: readonly [number, string, string][] = [
  [0, '#f3e6b8', '#dcc68a'],
  [0.37, '#f0bf5c', '#c98a2c'],
  [0.66, '#c77c2c', '#8f5220'],
  [0.9, '#7a4a1e', '#50300f'],
  [1, '#3a2a1c', '#1f1610'],
];

function rampColors(p: number): [string, string] {
  for (let i = 0; i < RAMP.length - 1; i++) {
    const a = RAMP[i]!;
    const b = RAMP[i + 1]!;
    if (p <= b[0]) {
      const t = (p - a[0]) / (b[0] - a[0]);
      return [mixColor(a[1], b[1], t), mixColor(a[2], b[2], t)];
    }
  }
  const last = RAMP[RAMP.length - 1]!;
  return [last[1], last[2]];
}

function drawSteel(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed: number,
  heat: number,
): void {
  const base = mixColor('#3b3f45', '#4a3a30', heat * 0.5);
  px(ctx, 0, 0, w, h, base);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const n = noise(x, y, seed);
      if (n > 0.93) px(ctx, x, y, 1, 1, '#50555c');
      else if (n < 0.04) px(ctx, x, y, 1, 1, '#2c2f34');
    }
  }
  // Grease sheen
  for (let i = 0; i < w; i++) {
    const y = Math.floor(h * 0.3 + Math.sin(i / 7 + seed) * 3);
    if (noise(i, 1, seed) > 0.5) px(ctx, i, y, 1, 1, '#5d636b');
  }
  px(ctx, 0, 0, w, 1, '#24272b');
  px(ctx, 0, h - 1, w, 1, '#1b1d20');
}

export interface GrillSlotView {
  occupied: boolean;
  /** 0 … 1 through the whole cook (1 = burnt threshold). */
  progress: number;
  toppings: readonly Topping[];
  seed: number;
  time: number;
  burnt: boolean;
  selected: boolean;
}

function inPatty(
  x: number,
  y: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  seed: number,
): boolean {
  const dx = (x - cx) / rx;
  const dy = (y - cy) / ry;
  const d = dx * dx + dy * dy;
  return d < 1 - noise(x, y, seed + 3) * 0.25;
}

export function drawGrillSlot(ctx: CanvasRenderingContext2D, v: GrillSlotView): void {
  const w = SLOT_W;
  const h = SLOT_H;
  ctx.clearRect(0, 0, w, h);
  drawSteel(ctx, w, h, v.seed, v.occupied ? Math.min(1, v.progress * 1.5) : 0);
  if (!v.occupied) {
    // faint scraper marks
    for (let i = 6; i < w - 6; i += 5) px(ctx, i, h / 2, 3, 1, '#474b51');
    return;
  }
  const cx = w / 2;
  const cy = h / 2 + 1;
  const rx = 17;
  const ry = 10;
  const [lightC, darkC] = rampColors(v.progress);

  // Shredded potato texture: short diagonal strands.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!inPatty(x, y, cx, cy, rx, ry, v.seed)) continue;
      const strand = noise(Math.floor((x + y * 0.6) / 2), y, v.seed) > 0.45;
      const fleck = noise(x, y, v.seed + 11) > 0.9;
      px(ctx, x, y, 1, 1, fleck ? mixColor(lightC, '#ffffff', 0.25) : strand ? lightC : darkC);
    }
  }

  const set = new Set(v.toppings);
  const scatter = (
    count: number,
    salt: number,
    fn: (x: number, y: number, i: number) => void,
  ): void => {
    for (let i = 0; i < count; i++) {
      const a = noise(i, salt, v.seed) * Math.PI * 2;
      const r = Math.sqrt(noise(salt, i, v.seed)) * 0.85;
      fn(Math.round(cx + Math.cos(a) * rx * r), Math.round(cy + Math.sin(a) * ry * r), i);
    }
  };
  if (set.has('smothered'))
    scatter(14, 1, (x, y, i) => px(ctx, x, y, 2, 1, i % 2 ? '#f7efd6' : '#e9dcaf'));
  if (set.has('chunked'))
    scatter(8, 2, (x, y) => {
      px(ctx, x, y, 2, 2, '#e98a8a');
      px(ctx, x, y, 2, 1, '#f4b1b1');
    });
  if (set.has('diced'))
    scatter(8, 3, (x, y) => {
      px(ctx, x, y, 2, 2, '#d8342c');
      px(ctx, x, y, 1, 1, '#ff6f61');
    });
  if (set.has('capped'))
    scatter(7, 4, (x, y) => {
      px(ctx, x, y, 3, 1, '#b08865');
      px(ctx, x + 1, y + 1, 1, 1, '#e9d7bd');
    });
  if (set.has('peppered'))
    scatter(7, 5, (x, y) => {
      px(ctx, x, y, 3, 2, '#2e7d32');
      px(ctx, x + 1, y, 1, 1, '#b8e986');
    });
  if (set.has('topped')) {
    for (let y = -4; y <= 4; y++)
      for (let x = -7; x <= 7; x++) {
        if ((x * x) / 49 + (y * y) / 16 > 1 - noise(x, y, v.seed + 5) * 0.3) continue;
        px(
          ctx,
          cx - 4 + x,
          cy - 1 + y,
          1,
          1,
          noise(x, y, v.seed + 6) > 0.8 ? '#5a1c0c' : '#8e2f14',
        );
      }
  }
  if (set.has('covered')) {
    for (let y = -5; y <= 5; y++)
      for (let x = -10; x <= 10; x++) {
        if ((x * x) / 100 + (y * y) / 25 > 1 - noise(x, y, v.seed + 7) * 0.45) continue;
        if (noise(x, y, v.seed + 8) > 0.82) continue;
        px(ctx, cx + x, cy + y - 1, 1, 1, noise(x, y, v.seed + 9) > 0.7 ? '#ffd35c' : '#f5a623');
      }
  }
  if (set.has('country')) {
    for (let y = -3; y <= 3; y++)
      for (let x = -6; x <= 6; x++) {
        if ((x * x) / 36 + (y * y) / 9 > 1 - noise(x, y, v.seed + 12) * 0.3) continue;
        const pepper = noise(x, y, v.seed + 13) > 0.86;
        px(ctx, cx + 5 + x, cy + 1 + y, 1, 1, pepper ? '#3b3b3b' : '#efe6d2');
      }
  }

  // Sizzle bubbles around the edge
  const t = Math.floor(v.time * 8);
  for (let i = 0; i < 6; i++) {
    const a = noise(i, t, v.seed) * Math.PI * 2;
    const sx = Math.round(cx + Math.cos(a) * (rx + 1));
    const sy = Math.round(cy + Math.sin(a) * (ry + 1));
    if (noise(t, i, v.seed) > 0.4) px(ctx, sx, sy, 1, 1, v.burnt ? '#6d6d6d' : '#fff8e1');
  }

  if (v.burnt) {
    const puff = (v.time * 10) % 30;
    for (let i = 0; i < 3; i++) {
      const y = h - ((puff + i * 10) % 30);
      const x = cx - 6 + i * 6 + Math.round(Math.sin(v.time * 3 + i) * 2);
      px(ctx, x, y, 3, 2, 'rgba(90,90,90,0.8)');
      px(ctx, x + 1, y - 1, 2, 1, 'rgba(140,140,140,0.6)');
    }
  }
}

export interface IronView {
  kind: WaffleKind | null;
  status: 'empty' | 'cooking' | 'ready' | 'burnt';
  progress: number;
  time: number;
  powered: boolean;
}

export function drawIron(ctx: CanvasRenderingContext2D, v: IronView): void {
  const w = IRON_W;
  const h = IRON_H;
  ctx.clearRect(0, 0, w, h);
  px(ctx, 0, 0, w, h, '#2a2c30');
  px(ctx, 1, 1, w - 2, h - 2, '#34373c');
  const cx = w / 2;
  const cy = h / 2;
  const r = 11;

  if (v.status === 'cooking' || (v.status === 'empty' && !v.powered)) {
    // Closed lid
    for (let y = -r; y <= r; y++)
      for (let x = -r; x <= r; x++) {
        const d = x * x + y * y;
        if (d > r * r) continue;
        const rim = d > (r - 2) * (r - 2);
        px(
          ctx,
          cx + x,
          cy + y,
          1,
          1,
          rim ? '#17181b' : noise(x, y, 4) > 0.9 ? '#3a3d42' : '#23252a',
        );
      }
    px(ctx, cx - 7, cy - 1, 14, 2, '#5b5f66');
    px(ctx, cx - 7, cy - 1, 14, 1, '#7a7f87');
    if (v.status === 'cooking' && v.powered) {
      const t = v.time * 6;
      for (let i = 0; i < 3; i++) {
        const y = Math.round(cy - 4 - ((t * 2 + i * 5) % 12));
        const x = Math.round(cx - 5 + i * 5 + Math.sin(t + i) * 1.5);
        px(ctx, x, y, 2, 1, 'rgba(255,255,255,0.55)');
      }
    }
  } else if (v.status === 'empty') {
    for (let y = -r; y <= r; y++)
      for (let x = -r; x <= r; x++) {
        const d = x * x + y * y;
        if (d > r * r) continue;
        const grid = (x + r) % 3 === 0 || (y + r) % 3 === 0;
        px(
          ctx,
          cx + x,
          cy + y,
          1,
          1,
          d > (r - 1) * (r - 1) ? '#17181b' : grid ? '#1f2125' : '#2b2e33',
        );
      }
  } else {
    drawWaffle(ctx, cx, cy, r - 1, v.kind ?? 'classic', v.status === 'burnt' ? 1.5 : 1);
    if (v.status === 'burnt') {
      const t = (v.time * 12) % 24;
      for (let i = 0; i < 3; i++) {
        const y = h - ((t + i * 8) % 24);
        px(ctx, cx - 6 + i * 6, y, 3, 2, 'rgba(80,80,80,0.85)');
      }
    }
  }

  // Indicator light
  const blink = Math.floor(v.time * 4) % 2 === 0;
  const light = !v.powered
    ? '#3a1d1d'
    : v.status === 'ready'
      ? '#3dff7a'
      : v.status === 'burnt'
        ? blink
          ? '#ff3b30'
          : '#5a1511'
        : v.status === 'cooking'
          ? '#ff9f1c'
          : '#444';
  px(ctx, w - 5, 2, 3, 3, light);
  if (v.powered && v.status !== 'empty') px(ctx, w - 5, 2, 1, 1, '#ffffff');
}
